import React from 'react';

interface FormattedTextProps {
  text: string;
  className?: string;
}

// Regex to match @[uuid|Display Name] and @[everyone] (case-insensitive UUID)
const MENTION_PATTERN = /@\[([a-fA-F0-9-]+)\|([^\]]+)\]|@\[everyone\]/gi;

let keyCounter = 0;

/**
 * Parse inline formatting markers within a single text segment.
 * Handles: **bold**, *italic*, __underline__, and @mentions.
 * Returns an array of React nodes.
 */
function parseInline(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  // Combined regex: markdown links, bold, underline, italic, mentions, plain URLs,
  // and backslash escapes (\[ \] \* etc. from text pasted out of markdown editors)
  const inlineRegex = /(\[([^\]]+)\]\((https?:\/\/[^)]+)\))|(\*\*(.+?)\*\*)|(__(.+?)__)|(\*(.+?)\*)|(@\[([a-fA-F0-9-]+)\|([^\]]+)\])|(@\[everyone\])|(https?:\/\/[^\s<>)\]]+)|(\\([\\`*_{}[\]()#+\-.!|~<>]))/g;
  let lastIndex = 0;
  let match;

  while ((match = inlineRegex.exec(text)) !== null) {
    // Add plain text before this match
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    if (match[1]) {
      // Markdown link: [text](url)
      nodes.push(
        <a key={`link-${keyCounter++}`} href={match[3]} target="_blank" rel="noopener noreferrer"
          style={{ color: '#0C6B62', textDecoration: 'underline', fontWeight: 500 }}>
          {match[2]}
        </a>
      );
    } else if (match[4]) {
      // Bold: **text**
      nodes.push(<strong key={`b-${keyCounter++}`}>{parseInline(match[5])}</strong>);
    } else if (match[6]) {
      // Underline: __text__
      nodes.push(<u key={`u-${keyCounter++}`}>{parseInline(match[7])}</u>);
    } else if (match[8]) {
      // Italic: *text*
      nodes.push(<em key={`i-${keyCounter++}`}>{parseInline(match[9])}</em>);
    } else if (match[13]) {
      // @[everyone]
      nodes.push(
        <span
          key={`me-${keyCounter++}`}
          style={{
            background: 'linear-gradient(135deg, #E1F5EE, #D4E8F7)',
            color: '#0F6E56',
            padding: '1px 6px',
            borderRadius: '4px',
            fontWeight: 600,
            fontSize: '0.95em',
          }}
        >
          @Team
        </span>
      );
    } else if (match[10]) {
      // @[uuid|Name]
      nodes.push(
        <span
          key={`m-${keyCounter++}`}
          style={{
            background: '#E1F5EE',
            color: '#0F6E56',
            padding: '1px 6px',
            borderRadius: '4px',
            fontWeight: 600,
            fontSize: '0.95em',
          }}
        >
          @{match[12]}
        </span>
      );
    } else if (match[15]) {
      // Escaped punctuation: render the character itself
      nodes.push(match[16]);
    } else if (match[14]) {
      // Plain URL
      const url = match[14];
      let display = url;
      try {
        const u = new URL(url);
        display = u.hostname + (u.pathname.length > 1 ? u.pathname.slice(0, 30) + (u.pathname.length > 30 ? '...' : '') : '');
      } catch {}
      nodes.push(
        <a key={`url-${keyCounter++}`} href={url} target="_blank" rel="noopener noreferrer"
          style={{ color: '#0C6B62', textDecoration: 'underline', fontWeight: 500 }}>
          {display}
        </a>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
}

/**
 * FormattedText component that renders plain text with markdown-style
 * formatting markers as styled React nodes.
 *
 * Supports:
 * - **bold** -> <strong>
 * - *italic* -> <em>
 * - __underline__ -> <u>
 * - Lines starting with "* ", "- " or "• " -> bullet list
 * - Lines starting with "1. " -> numbered list
 * - @[uuid|Name] and @[everyone] -> styled mention spans
 */
const FormattedText: React.FC<FormattedTextProps> = ({ text, className }) => {
  if (!text) return null;
  keyCounter = 0;

  // Strip trailing `\` at end of lines — markdown hard-break syntax that older
  // editor output wrote into list items. Avoids rendering a literal backslash.
  const lines = text.split('\n').map(l => l.replace(/\\+$/, ''));
  const elements: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Check for bullet list (lines starting with "* ", "- " or "\u2022 ")
    if (/^[\u2022\*-]\s/.test(line)) {
      const listItems: React.ReactNode[] = [];
      while (i < lines.length && /^[\u2022\*-]\s/.test(lines[i])) {
        const content = lines[i].replace(/^[\u2022\*-]\s/, '');
        listItems.push(
          <li key={`li-${keyCounter++}`} style={{ marginBottom: '2px' }}>
            {parseInline(content)}
          </li>
        );
        i++;
      }
      elements.push(
        <ul key={`ul-${keyCounter++}`} style={{ margin: '4px 0', paddingLeft: '20px' }}>
          {listItems}
        </ul>
      );
      continue;
    }

    // Check for numbered list (lines starting with "N. ")
    if (/^\d+\.\s/.test(line)) {
      const listItems: React.ReactNode[] = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        const content = lines[i].replace(/^\d+\.\s/, '');
        listItems.push(
          <li key={`oli-${keyCounter++}`} style={{ marginBottom: '2px' }}>
            {parseInline(content)}
          </li>
        );
        i++;
      }
      elements.push(
        <ol key={`ol-${keyCounter++}`} style={{ margin: '4px 0', paddingLeft: '20px' }}>
          {listItems}
        </ol>
      );
      continue;
    }

    // Regular line
    if (i > 0 && elements.length > 0) {
      elements.push(<br key={`br-${keyCounter++}`} />);
    }
    if (line) {
      elements.push(<React.Fragment key={`line-${keyCounter++}`}>{parseInline(line)}</React.Fragment>);
    }
    i++;
  }

  return <span className={className}>{elements}</span>;
};

export default FormattedText;
